import { DataKeyValueStringInterface } from 'pf-frontend-common/dist/module/data/key-value/string.interface';
import { JsonApiDataInterface } from 'pf-frontend-common/dist/module/json-api/data.interface';
import { domQuerySelector } from 'pf-frontend-common/src/helper/dom/query-selector';
import { JsonApiPayloadInterface } from 'pf-frontend-common/src/module/json-api/payload.interface';
import { ViewBasicInterface } from 'pf-frontend-common/src/module/view/basic.interface';

import { AgentStore } from 'common/data/agent/store';
import { BackendConfigSettingsInterface } from 'common/data/backend/config/settings.interface';
import { LocationModel } from 'common/data/location/model';
import { configGetLanguage } from 'common/helper/config/get-language';
import { languageGetName } from 'common/helper/language/get-name';
import { PreactMarkupComponentsInterface } from 'common/helper/preact/markup/components.interface';
import { settingsGetPageType } from 'common/helper/settings/get-page-type';
import { AgentListCommonViewStore } from 'common/module/agent/list-common/view-store';
import { BuildingReviewAgentSwitchViewStore } from 'common/module/building-review/agent-switch/view-store';
import { MintagaReviewView } from 'common/module/building-review/mintaga-review/view';
import { MintagaReviewCssSelectorEnum } from 'common/module/building-review/page/css-selector.enum';
import { BuildingReviewPageViewOptionsInterface } from 'common/module/building-review/page/view-options.interface';
import { DataStoreEvent } from 'common/module/data/store.event';
import { MapMarkerViewInterface } from 'common/module/map/marker/view.interface';
import { MapMarkerViewOptionsInterface } from 'common/module/map/marker/view-options.interface';
import { MapViewEvent } from 'common/module/map/view.event';
import { StatsGtmProviderInterface } from 'common/module/stats/gtm/provider.interface';
import { StatsSnowplow2ActionEnum } from 'common/module/stats/snowplow2/action.enum';
import { StatsSnowplow2ProviderInterface } from 'common/module/stats/snowplow2/provider.interface';
import { UserPopupLoginStatusEnum } from 'common/module/user/popup/login-status.enum';
import { ViewDynamicInterface } from 'common/module/view/dynamic.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';
import { preactMarkup } from 'common/typings/preact-markup';

import { BuildingReviewPageAgentListOptionsInterface } from './agent-list-options.interface';
import { BuildingReviewPageAgentListTypeEnum } from './agent-list-type.enum';
import { BuildingReviewSettingsInterface } from './settings.interface';

export class BuildingReviewPageView implements ViewBasicInterface {
  /**
   * View main element
   */
  private element: HTMLElement;

  /**
   * Agent sale/rent switch element
   */
  private agentSwitch: HTMLElement;

  /**
   * Agent rent main element
   */
  private agentRent: HTMLElement;

  /**
   * Agent sale main element
   */
  private agentSale: HTMLElement;

  /**
   * Location model represented by this view
   */
  private locationModel: LocationModel;

  /**
   * Mintaga review main element
   */
  private mintagaReviews: HTMLElement;

  /**
   * CSS Classes
   */
  private cssClass: DataKeyValueStringInterface = {
    hideSwitch: 'building-specialist__switch-area--hidden',
    hideAgent: 'building-specialist__featured-agent--hidden',
  };

  /**
   * Constructor
   */
  constructor(
    private mapView: MapMarkerViewInterface,
    private preactMarkupHelper: preactMarkup,
    private components: PreactMarkupComponentsInterface,
    private userAuthenticationService: UserAuthenticationServiceInterface,
    protected statsGtm: StatsGtmProviderInterface,
    protected statsSnowplow2: StatsSnowplow2ProviderInterface,
    protected settings: BackendConfigSettingsInterface,
    protected agentSwitchViewDynamic: ViewDynamicInterface<BuildingReviewAgentSwitchViewStore>,
    protected agentRentStore: AgentStore,
    protected agentSaleStore: AgentStore,
    protected buildingReviewPageSettings: BuildingReviewSettingsInterface,
    protected agentRentListViewDynamic: ViewDynamicInterface<AgentListCommonViewStore>,
    protected agentSaleListViewDynamic: ViewDynamicInterface<AgentListCommonViewStore>,
    private mintagaReviewView: MintagaReviewView
  ) {}

  /**
   * @inheritDoc
   */
  public initialize(options: BuildingReviewPageViewOptionsInterface): void {
    this.element = options.element;
    this.agentSwitch = options.buildingSpecialist.agentSwitch;
    this.agentRent = options.buildingSpecialist.rent;
    this.agentSale = options.buildingSpecialist.sale;

    this.locationModel = options.locationModel;

    this.mintagaReviews = domQuerySelector(this.element, MintagaReviewCssSelectorEnum.mintagaview);

    this.initMap(options.subview.map.viewOptions);
    this.initAgentSwitch();

    this.initRentAgentList();
    this.initSaleAgentList();

    this.initMintagaReviews();

    if (this.agentSwitch) {
      this.updateAgentListVisibility(this.agentSwitchViewDynamic.getViewStore().getState().isSaleCategoryActive);
    }
    // Attach observers
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public render(): void {
    this.stats();

    this.preactMarkupHelper(this.element, this.components);

    if (this.agentSwitch) {
      this.agentSwitchViewDynamic.render();
    }

    if (this.agentRent && this.getAgentList(BuildingReviewPageAgentListTypeEnum.RENT)) {
      this.agentRentListViewDynamic.render();
    }

    if (this.agentSale && this.getAgentList(BuildingReviewPageAgentListTypeEnum.SALE)) {
      this.agentSaleListViewDynamic.render();
    }
  }

  /**
   * @inheritdoc
   */
  private observers(): void {
    this.agentSwitchViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(DataStoreEvent.updateState, this.onAgentSwitchUpdateState);
  }

  /**
   * Log stats for the page
   */
  private stats(): void {
    const userModel = this.userAuthenticationService.getUser();

    this.statsGtm.send({
      language: languageGetName(configGetLanguage().current),
      pageType: settingsGetPageType(this.settings),
      pageCategory: '',
      userId: userModel ? parseInt(userModel.id, 10) : '',
      userStatus: userModel && userModel.id ? UserPopupLoginStatusEnum.loggedIn : UserPopupLoginStatusEnum.notLoggedIn,
    });

    this.statsSnowplow2.send({
      action: StatsSnowplow2ActionEnum.trackPageView,
      options: {
        userId: userModel ? userModel.id : '',
      },
    });
  }

  /**
   * Initialize map
   */
  private initMap(options: MapMarkerViewOptionsInterface): void {
    this.mapView.initialize(options);

    this.mapView.getEventEmitter().addListener(MapViewEvent.render, this.onRenderMapView);
  }

  /**
   * Initialize mintaga reviews
   */
  private initMintagaReviews(): void {
    if (!this.mintagaReviews) {
      return;
    }

    this.mintagaReviewView.initialize({
      element: this.mintagaReviews,
    });
  }

  /**
   * Initialize agent rent/sale toggle
   */

  private initAgentSwitch(): void {
    if (!this.agentSwitch) {
      return;
    }

    const agentRentPayload = this.getAgentList(BuildingReviewPageAgentListTypeEnum.RENT);
    const agentSalePayload = this.getAgentList(BuildingReviewPageAgentListTypeEnum.SALE);

    if (agentRentPayload && agentSalePayload) {
      this.agentSwitch.classList.remove(this.cssClass.hideSwitch);
    }

    this.agentSwitchViewDynamic.initialize({
      element: this.agentSwitch,
    });
    this.agentSwitchViewDynamic.getViewStore().initialize({
      isSaleCategoryActive: !!agentSalePayload,
    });
  }

  /**
   * Initialize list of rent agents
   */
  private initRentAgentList(): void {
    this.initAgentList({
      agentStore: this.agentRentStore,
      agentPayload: this.getAgentList(BuildingReviewPageAgentListTypeEnum.RENT),
      agentListDynamicView: this.agentRentListViewDynamic,
      agentListElement: this.agentRent,
      forSale: false,
    });
  }

  /**
   * Initialize list of sale agents
   */
  private initSaleAgentList(): void {
    this.initAgentList({
      agentStore: this.agentSaleStore,
      agentPayload: this.getAgentList(BuildingReviewPageAgentListTypeEnum.SALE),
      agentListDynamicView: this.agentSaleListViewDynamic,
      agentListElement: this.agentSale,
      forSale: true,
    });
  }

  /**
   * Initialize list of agents
   */
  private initAgentList(options: BuildingReviewPageAgentListOptionsInterface): void {
    const { agentPayload, agentListDynamicView, agentStore, agentListElement, forSale } = options;

    if (!agentPayload || !(agentPayload.data as JsonApiDataInterface[]).length) {
      return;
    }

    const agentListDynamicViewStore = agentListDynamicView.getViewStore();

    agentStore.initialize({
      payload: agentPayload,
    });
    agentListDynamicView.initialize({
      element: agentListElement,
    });
    agentListDynamicViewStore.initialize();
    agentListDynamicViewStore.updateAgents(agentStore.getAll(), {});
    agentListDynamicViewStore.updateHtmlElement(0, agentListElement);

    agentListDynamicViewStore.updateAgentAdInfo({
      forSale,
    });
  }

  /**
   * Updates visibility of agent lists
   */
  private updateAgentListVisibility(forSale: boolean): void {
    if (forSale) {
      this.agentRent.classList.add(this.cssClass.hideAgent);
      this.agentSale.classList.remove(this.cssClass.hideAgent);
    } else {
      this.agentRent.classList.remove(this.cssClass.hideAgent);
      this.agentSale.classList.add(this.cssClass.hideAgent);
    }
  }

  /**
   * Get agents list payload
   * If building specialist product is disabled for the building
   * Then backend return null in agents list
   * If building specialist product is enabled for the building but we dont have any agent
   * Then backend return empty payload
   */
  private getAgentList(listType: BuildingReviewPageAgentListTypeEnum): JsonApiPayloadInterface {
    const list = this.buildingReviewPageSettings.agents[listType];
    return list && list.payload && (list.payload.data as JsonApiDataInterface[]).length ? list.payload : null;
  }

  /**
   * The map has been rendered
   */
  private onRenderMapView = (): void => {
    // Add a marker
    this.mapView.addMarker(this.locationModel.coordinates);
  };

  /**
   * Agent switch state has been updated
   */
  private onAgentSwitchUpdateState = () => {
    this.updateAgentListVisibility(this.agentSwitchViewDynamic.getViewStore().getState().isSaleCategoryActive);
  };
}
